import { ApolloError } from "@apollo/client";
import { formatNumber, getLocaleConstants } from "@tosize/ui";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { getWebsite } from "~/graphql/shared/website";
import domains from "./../../domains.json";

export function getPositiveNegativeItems(type: "-" | "+", value?: string) {
	return value
		?.split(/\r?\n/)
		.filter((item) => item.slice(0, 3).includes(type))
		.map((item) => item.replace(`${type} `, ""));
}

export function getCurrentUTCTime() {
	const now = new Date();
	const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
	return utc.getTime();
}

export function mapValidationErrorsToForm<T extends FieldValues>(
	error: unknown | ApolloError,
	setError: UseFormSetError<T>,
) {
	if (error instanceof ApolloError) {
		const validationError = error.graphQLErrors.find(
			(error) => error?.extensions?.category === "validation",
		);

		if (validationError) {
			for (const [key, message] of Object.entries(validationError.extensions.validation)) {
				setError(key as Path<T>, { type: "manual", message: message as string });
			}
		}
	}
}

export function getDomains(lang: string) {
	const countries = domains.flatMap((item) => ({
		href: `https://${item.domain}`,
		locale: item.defaultLocale,
	}));

	const countryDomains = domains.flatMap((item) =>
		item.locales.map((locale) => {
			const base = `https://${item.domain}`;
			const href = locale === item.defaultLocale && !item.alwaysPrefix ? base : `${base}/${locale}`;

			return { href, locale };
		}),
	);

	const current = domains.find((item) => item.locales.includes(lang));
	const languages = current?.locales.map((locale) => ({
		href: `https://${current.domain}/${locale}`,
		locale: locale,
	}));

	return { countries, languages, domains: countryDomains };
}

export function getHrefLangs(lang: string) {
	return Array.from(document.querySelectorAll("head > link[hreflang]"))
		.map((item) => {
			const locale = item.getAttribute("hreflang") ?? undefined;
			const href = item.getAttribute("href") ?? undefined;

			return locale && href ? { locale, href } : undefined;
		})
		.filter(Boolean)
		.find((item) => item.locale === lang);
}

export async function getKiyohTotalScore(lang: string) {
	const { current } = await getWebsite(lang);
	const { kiyoh } = getLocaleConstants(lang);

	const totalScore = current.kiyoh.totalScore ?? 10;
	const value = kiyoh.totalScore === 5 ? totalScore / 2 : totalScore;
	const score = formatNumber({ lang, value, style: "decimal", digits: { min: 0, max: 1 } });

	return `${score}/${kiyoh.totalScore}`;
}
